// Owns the single off-thread bake worker and exposes a promise-based one-shot
// request. The worker is created lazily on first use; if it can't be spawned
// (environment without module workers), requestBake resolves null and callers
// fall back to the synchronous on-the-fly bake — the asset is an optional cache,
// so the app stays correct, just slower for big models.

import type { BakeRequestMsg, BakeResultMsg, BakedAssetCore } from "./bake-protocol";
import type { SavedVoxels } from "../model/saved-model";

let worker: Worker | null = null;
let spawnFailed = false;
let nextId = 1;

function getWorker(): Worker | null {
  if (worker || spawnFailed) return worker;
  try {
    worker = new Worker(new URL("./bake.worker.ts", import.meta.url), { type: "module" });
  } catch {
    spawnFailed = true;
  }
  return worker;
}

// Bake `voxels` off-thread. `includeAO` false → shell only (fast, the save
// path); true → with the AO mesh (on-demand for presentation). Resolves with the
// asset core, or null if the worker is unavailable or errors. Concurrent
// requests are paired to their results by id.
export function requestBake(
  voxels: SavedVoxels,
  includeAO: boolean,
): Promise<BakedAssetCore | null> {
  const w = getWorker();
  if (!w) return Promise.resolve(null);
  const id = nextId++;
  return new Promise((resolve) => {
    const done = (value: BakedAssetCore | null): void => {
      w.removeEventListener("message", onMsg);
      w.removeEventListener("error", onErr);
      resolve(value);
    };
    const onMsg = (e: MessageEvent<BakeResultMsg>): void => {
      if (e.data.id !== id) return; // another request's result
      const { id: _id, ...core } = e.data;
      done(core);
    };
    const onErr = (): void => done(null);
    w.addEventListener("message", onMsg);
    w.addEventListener("error", onErr);
    w.postMessage({ id, voxels, includeAO } satisfies BakeRequestMsg);
  });
}
